import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { theme } from '@actual-app/components/theme';
import { tokens } from '@actual-app/components/tokens';
import { View } from '@actual-app/components/view';
import { css } from '@emotion/css';

import { FinancialText } from '#components/FinancialText';
import type { ReadyFinancialDashboard } from '#components/product-finance/client';
import {
  formatProductDate,
  formatProductMoney,
} from '#components/product-finance/money';
import type { ProductMoney } from '#components/product-finance/money';

export function CommitmentRow({
  item,
  badge,
  onRecord,
}: {
  item: Readonly<{
    occurrence: Readonly<{
      id: string;
      name: string;
      amount: ProductMoney;
      date: string;
    }>;
  }>;
  badge: ReactNode;
  onRecord: () => void;
}) {
  return (
    <View
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto auto',
        alignItems: 'center',
        gap: 12,
        padding: '11px 0',
        borderBottom: `1px solid ${theme.tableBorder}`,
        [`@media (max-width: ${tokens.breakpoint_small})`]: {
          gridTemplateColumns: 'minmax(0, 1fr) auto',
        },
      }}
    >
      <View style={{ minWidth: 0 }}>
        <strong>{item.occurrence.name}</strong>
        <span style={{ color: theme.pageTextSubdued, fontSize: 12 }}>
          {formatProductDate(item.occurrence.date)} · {badge}
        </span>
      </View>
      <FinancialText as="strong">
        {formatProductMoney(item.occurrence.amount)}
      </FinancialText>
      <Button
        onPress={onRecord}
        className={css({
          minHeight: 38,
          [`@media (max-width: ${tokens.breakpoint_small})`]: {
            gridColumn: '1 / -1',
            width: '100%',
          },
        })}
      >
        <Trans>Record payment</Trans>
      </Button>
    </View>
  );
}

export function PaidCommitmentRow({
  item,
  onUndo,
}: {
  item: Extract<
    ReadyFinancialDashboard['paidCurrentCycleCommitments'][number],
    Readonly<{ status: 'matched' }>
  >;
  onUndo: () => void;
}) {
  const { t } = useTranslation();
  const variance = item.amountVariance.minorUnits;
  return (
    <View
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto auto',
        alignItems: 'center',
        gap: 12,
        padding: '11px 0',
        borderBottom: `1px solid ${theme.tableBorder}`,
      }}
    >
      <View style={{ minWidth: 0 }}>
        <strong>{item.occurrence.name}</strong>
        <span style={{ color: theme.pageTextSubdued, fontSize: 12 }}>
          <Trans>Paid</Trans> {formatProductDate(item.actualFact.date)}
          {variance !== 0 && (
            <>
              {' '}
              ·{' '}
              {variance > 0 ? (
                <Trans>Above expected</Trans>
              ) : (
                <Trans>Below expected</Trans>
              )}{' '}
              {formatProductMoney({
                ...item.amountVariance,
                minorUnits: Math.abs(variance),
              })}
            </>
          )}
        </span>
      </View>
      <FinancialText as="strong">
        {formatProductMoney(item.actualFact.amount)}
      </FinancialText>
      <Button
        onPress={() => {
          if (globalThis.confirm(t('Undo this confirmation?'))) onUndo();
        }}
        style={{ minHeight: 38 }}
      >
        <Trans>Undo</Trans>
      </Button>
    </View>
  );
}
